// One status map for the whole embed: (domain, raw wire value) -> the human
// label and the tone a badge paints. Every panel that shows a status reads it
// from here, so the same word never carries two colours and a raw snake_case
// enum never reaches a customer.

export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral";

export type StatusDomain =
  | "order"
  | "payment"
  | "subscription"
  | "mid"
  | "referral"
  | "redemption"
  | "rma"
  | "invitation"
  | "member";

export interface StatusPresentation { label: string; tone: StatusTone }

const EM_DASH = "—";

/** "no_response" -> "No response". An empty value reads as an em dash. */
export function humanizeEnum(value: string): string {
  const spaced = value.replace(/_/g, " ").trim();
  if (spaced === "") return EM_DASH;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

type Row = [label: string, tone: StatusTone];

// Values come from the wire enums the panels already read: OrderStatus
// (embed-shared/types.ts), payment status, SubscriptionStatus, and the
// comments on MerchantMID / ReferralWire / ReferralRedemptionWire / Rma /
// MembershipWire (roles). InvitationWire carries no status field: the pending
// list is pending-only, and "expired" / "revoked" are derived on the client
// from expires_at / revoked_at, so those keys are the derived states. The redemption domain backs the
// "Redemptions" tab (the list formerly labelled "Payouts").
const MAP: Record<StatusDomain, Record<string, Row>> = {
  order: {
    pending: ["Pending", "warning"],
    pending_payment: ["Awaiting payment", "warning"],
    confirmed: ["Confirmed", "info"],
    processing: ["Processing", "info"],
    shipped: ["Shipped", "info"],
    delivered: ["Delivered", "success"],
    cancelled: ["Cancelled", "danger"],
    refunded: ["Refund recorded", "neutral"],
  },
  payment: {
    pending: ["Pending", "warning"],
    // authorized = funds held, not yet captured: in progress, not done.
    authorized: ["Authorized", "info"],
    paid: ["Paid", "success"],
    captured: ["Paid", "success"],
    failed: ["Failed", "danger"],
    voided: ["Voided", "neutral"],
    partially_refunded: ["Partial refund recorded", "neutral"],
    refunded: ["Refund recorded", "neutral"],
  },
  subscription: {
    active: ["Active", "success"],
    canceled: ["Ended", "neutral"],
  },
  mid: {
    pending: ["Pending verification", "warning"],
    active: ["Active", "success"],
    closed: ["Closed", "neutral"],
    rejected: ["Rejected", "danger"],
  },
  referral: {
    submitted: ["Submitted", "info"],
    in_queue: ["In queue", "info"],
    engaging: ["Engaging", "info"],
    finalizing: ["Finalizing", "info"],
    completed: ["Completed", "success"],
    no_response: ["No response", "neutral"],
    abandoned: ["Abandoned", "neutral"],
    declined: ["Declined", "danger"],
    cancelled: ["Cancelled", "neutral"],
  },
  redemption: {
    pending: ["Pending", "warning"],
    in_progress: ["In progress", "info"],
    verified: ["Verified", "info"],
    applied: ["Applied", "success"],
    settled: ["Settled", "success"],
    rejected: ["Rejected", "danger"],
    cancelled: ["Cancelled", "neutral"],
    expired: ["Expired", "neutral"],
    revoked: ["Revoked", "neutral"],
  },
  rma: {
    requested: ["Requested", "warning"],
    approved: ["Approved", "success"],
    rejected: ["Rejected", "danger"],
    completed: ["Completed", "success"],
  },
  invitation: {
    pending: ["Pending", "warning"],
    accepted: ["Accepted", "success"],
    revoked: ["Revoked", "neutral"],
    expired: ["Expired", "neutral"],
  },
  member: {
    owner: ["Owner", "info"],
    manager: ["Manager", "neutral"],
    user: ["User", "neutral"],
    guest: ["Guest", "neutral"],
  },
};

/** The label and tone for a status; an unknown value is humanized and neutral, never raw. */
export function statusPresentation(domain: StatusDomain, value: string): StatusPresentation {
  const row = Object.prototype.hasOwnProperty.call(MAP[domain], value) ? MAP[domain][value] : undefined;
  if (row) return { label: row[0], tone: row[1] };
  return { label: humanizeEnum(value), tone: "neutral" };
}
